import React, { useContext, useState } from "react";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import ConversationsSidebarContainer from "@/components/chat/ConversationsSidebarContainer";
import ChatAreaChild from "@/components/chat/ChatAreaChild";
import { useGetChatRoomsQuery } from "@/api/chatApi/chat";
import { ChatContext, ChatContextInterface } from "@/components/MSRContext";
import Global from "@/globals/globals";
import type { ChatRoom } from "@/types/chat.types";
import generateRandomString from "../MSRP/utilities/generators";
import { createEmptyMultiPart, MessagePart } from "../utils/multiPartBuilder";
import { MyRequest } from "../models/MyRequest";

/**
 * ChatPage - New prop-based chat interface
 *
 * This page uses the parent-child architecture:
 * - ConversationsSidebarContainer manages room selection
 * - ChatAreaChild displays messages for selected room
 * - NO URL navigation - everything is prop-based
 */

const ChatPage: React.FC = () => {
  // Get MSRP wrapper, context functions, and rooms from WebSocket
  const { wrapper, handleaddServerRequest, rooms: contextRooms } = useContext(ChatContext) as ChatContextInterface;

  // Track selected room for MSRP operations
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);

  // Fetch rooms via RTK Query
  const { data, isLoading, refetch } = useGetChatRoomsQuery({
    personal: true,
    "chat-type": "any",
    "session-status": "both",
  });

  const rooms = data?.chatRooms || [];

  // Refetch rooms when new room is added via WebSocket
  React.useEffect(() => {
    if (contextRooms && contextRooms.length > 0) {
      console.log("📡 WebSocket rooms updated, refetching from REST API...");
      refetch();
    }
  }, [contextRooms.length, refetch]);

  // Delete message using MSRP protocol
  const handleDeleteMessage = (messageId: string, agentUsername?: string) => {
    if (!selectedRoom) return;

    const id = generateRandomString(16);
    const toName = selectedRoom.session_type_id === 3
      ? selectedRoom.session_key
      : selectedRoom.session_type === "c2a"
        ? selectedRoom?.client?.client_username + Global.companyDomain || ""
        : selectedRoom?.agent?.agent_username + Global.companyDomain || "";

    const payload = {
      id,
      type: "request",
      job: "delete-message",
      parameters: {
        "deleted-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "delete-time": Math.floor(Date.now() / 1000),
      },
    };

    wrapper?.sendMsg(JSON.stringify(payload, null, 4), "application/rechat.cntl+json");

    handleaddServerRequest?.(
      new MyRequest(
        id,
        Global.username + Global.companyDomain,
        toName,
        "delete-message",
        "pending",
        "This message has been deleted",
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Edit message using MSRP protocol
  const handleEditMessage = (messageId: string, newText: string) => {
    if (!selectedRoom) return;

    const toName =
      selectedRoom.session_type === "group-chat"
        ? selectedRoom.session_key
        : selectedRoom.session_type === "c2a"
          ? selectedRoom?.client?.client_username + Global.companyDomain || ""
          : selectedRoom?.agent?.agent_username + Global.companyDomain || "";

    const multipartMessage = createEmptyMultiPart();
    const reqID = generateRandomString(16);

    const messageJson = {
      id: reqID,
      type: "request",
      job: "edit-message",
      parameters: {
        "current-message-id": messageId,
        "from-name": Global.username + Global.companyDomain,
        "to-name": toName,
        "chat-type": selectedRoom.session_type,
      },
    };

    const messageJsonString = JSON.stringify(messageJson, null, 4);

    const part1 = new MessagePart({
      name: "part1",
      contentType: "application/rechat.cntl+json\r\n",
      filename: "file1.txt",
      value: messageJsonString,
      contentDisposition: "attachment",
    });

    const part2 = new MessagePart({
      name: "part2",
      contentType: "text/plain\r\n",
      filename: "file2.txt",
      value: newText,
      contentDisposition: "attachment",
    });

    multipartMessage.addPart(part1);
    multipartMessage.addPart(part2);

    const Body = multipartMessage.toString();
    const Boundary = multipartMessage.boundary;
    const Type = 'multipart/related;boundary="' + Boundary + '"';

    wrapper?.sendMsg(Body, Type);

    handleaddServerRequest?.(
      new MyRequest(
        reqID,
        Global.username + Global.companyDomain,
        selectedRoom.agent?.agent_username,
        "edit-message",
        "pending",
        newText,
        false,
        new Date().toISOString(),
        messageId
      )
    );
  };

  // Send message - handled by MessageInput component's MSRP logic
  const handleSendMessage = (text: string, sender: string, isOwner: boolean, id: string) => {
    console.log("Message sent (handled by MessageInput):", text);
  };

  // Send file message
  const handleSendFileMessage = (fileMessage: any) => {
    console.log("File message sent:", fileMessage.fileName);
  };

  const handleRoomSelect = (room: ChatRoom) => {
    console.log("✅ Room selected via PROPS (no URL change):", room.session_key);
    setSelectedRoom(room);
  };

  return (
    <NavbarSidebarLayout>
      <ConversationsSidebarContainer
        rooms={rooms}
        isLoading={isLoading}
        onRoomSelect={handleRoomSelect}
      >
        {(selectedRoom) => (
          <ChatAreaChild
            room={selectedRoom}
            wrapper={wrapper as any}
            currentUser={Global.username + Global.companyDomain}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onSendMessage={handleSendMessage}
            onSendFileMessage={handleSendFileMessage}
            onRefreshRooms={refetch}
          />
        )}
      </ConversationsSidebarContainer>
    </NavbarSidebarLayout>
  );
};

export default ChatPage;
